import { getPosition } from "./locationService";

export interface LatLon {
  lat: number;
  lon: number;
}

/**
 * Great-circle distance between two points, in whole miles.
 * Pure function — extracted from the old inline body of `distanceFromMe` so it
 * can be unit tested without touching the browser.
 *
 * @param {LatLon} a - first point
 * @param {LatLon} b - second point
 * @returns {number} distance in miles, rounded to the nearest mile
 */
export const haversineMiles = (a: LatLon, b: LatLon): number => {
  const lat1 = a.lat;
  const lon1 = a.lon;
  const lat2 = b.lat;
  const lon2 = b.lon;

  // Calculate distance using Haversine formula
  const R = 6371; // Radius of earth in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const c1 =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(c1), Math.sqrt(1 - c1));
  const distance = R * c;

  return Math.round(distance * 0.621371); // Convert to miles and round
};

/**
 * Calculates distance between the browser's current location and a given
 * latitude/longitude.
 *
 * Delegates to `locationService`, so N simultaneous callers (e.g. every row of
 * the list view) collapse into a SINGLE browser geolocation request, share a
 * 5-minute cache, and are bounded by an explicit 8s timeout.
 *
 * Failure contract: this ALWAYS resolves. On any geolocation failure it resolves
 * `null` rather than rejecting. (The previous implementation returned `null`
 * from inside a Promise executor when geolocation was absent, which meant the
 * promise never settled at all and every caller hung forever.)
 *
 * @param {number} lat - given latitude
 * @param {number} lon - given longitude
 * @returns {Promise<number | null>} distance in miles, or null when the current
 * location is unavailable
 * @example
 * distanceFromMe(50, 70)
 *  .then((distance) => console.log("distance between current location and latLon[50, 70]"))
 */
export const distanceFromMe = (
  lat: number, lon: number
): Promise<number | null> =>
  getPosition({
    source: "distance",
    maxAgeMs: 300000,
    timeoutMs: 8000,
    highAccuracy: false,
  })
    .then((position) => haversineMiles(
      { lat: position.lat, lon: position.lon },
      { lat, lon }
    ))
    .catch(() => null);
